<script setup lang="ts">
import { format } from "date-fns";

import {
    RespiratoryCondition, RespiratorySubCondition, PersonData, PrescribedConditionTypes, MedicalCondition,
} from "@/entities/conditions";
import { Suggestion } from "@/entities/suggestion";
import MedicalFormBuilder from "@/components/medicalConditionForms/MedicalFormBuilder.vue";
import { parseSchema } from "@/components/medicalConditionForms/schema";
import { DatePicker, Question } from "@/components/fields";
import MedicalConditionSearch from "@/components/medicalConditionForms/MedicalConditionSearch.vue";
import { EditableList } from "@/components/EditableList";
import validateRespiratoryForm from "./validators";
import { defaultDateFormat } from "@/constants/date";
import { useMedicalFormWithSubConditions } from "../useMedicalForm";
import respiratory from "./respiratory.json";

export interface Props {
    condition: RespiratoryCondition,
    personDetails: PersonData,
    subConditionSuggestion: Suggestion | null
}

const props = defineProps<Props>();

const emit = defineEmits<{
        (on:"update:condition", value: RespiratoryCondition): void
    }>();

const getInitialNewCondition = (props: Props): RespiratorySubCondition | null => {
    if (!props.condition.subConditions?.length) {
        return {
            syntheticId: Date.now(),
            id: Number(props.condition.id) ?? 0,
            type: PrescribedConditionTypes.Respiratory,
            name: props.condition.name,
            diagnosisdate: "",
            code: props.condition.code ?? null,
        };
    } else if (props.subConditionSuggestion) {
        return {
            syntheticId: Date.now(),
            ...props.subConditionSuggestion,
            id: Number(props.subConditionSuggestion.id) ?? 0,
            type: PrescribedConditionTypes.Respiratory,
            diagnosisdate: "",
            code: props.subConditionSuggestion.code ?? null,
        };
    } else {
        return null;
    }
};

const {
    onConditionUpdate,
    minSubConditionDate,
    state,
    handleUpdate,
    handleUpdateItem,
    handleSave,
} = useMedicalFormWithSubConditions<
    {type: PrescribedConditionTypes.Respiratory} & RespiratoryCondition, RespiratorySubCondition
>({
    condition: {
        ...props.condition,
        type: PrescribedConditionTypes.Respiratory,
        subConditions: props.condition.subConditions || [],
        medications: props.condition.medications || [],
    },
    newCondition: getInitialNewCondition(props),
}, emit);

const validators = validateRespiratoryForm();

const handleConditionSelect = (condition: MedicalCondition) => {
    state.newCondition = {
        ...condition,
        type: PrescribedConditionTypes.Respiratory,
        diagnosisdate: "",
        syntheticId: Date.now(),
    };
};

const currentDate = new Date();
const maxDate = currentDate;
const minDate = new Date(props.personDetails.dob);
</script>

<template>
    <p class="font-weight-bold-col">
        Declared respiratory conditions
    </p>
    <EditableList
        :current-item="state.newCondition"
        :items="condition.subConditions || []"
        :header="state.newCondition?.name"
        @update:items="handleUpdate"
        @update:item="handleUpdateItem"
        @save="handleSave"
    >
        <template
            v-for="item in condition.subConditions"
            #[item.syntheticId]
            :key="item.syntheticId"
        >
            <span
                class="col-md-2 "
            >
                <b>{{ item.name }}</b>
            </span>
        </template>
        <div
            v-if="state.newCondition !== null"
        >
            <p class="font-weight-bold-col">
                {{ state.newCondition.name }}
            </p>
            <Question
                name="diagnosis"
                label="When were you diagnosed?"
                tooltip="Please enter the date the condition was first diagnosed. If you don't know the month, please enter June as a default value."
            >
                <DatePicker
                    :key="state.newCondition.id"
                    v-model="state.newCondition.diagnosisdate"
                    :max-date="maxDate"
                    :min-date="minDate"
                    :show-day-field="false"
                    :validation="validators.diagnosisDate"
                />
            </Question>
        </div>
    </EditableList>
    <MedicalConditionSearch
        id="respiratory-condition"
        :key="condition.subConditions?.length"
        label="Add another respiratory condition"
        :type="PrescribedConditionTypes.Respiratory"
        :disable-add-not-found="true"
        @select:condition="handleConditionSelect"
    />
    <MedicalFormBuilder
        v-if="minSubConditionDate !== null"
        :schema="parseSchema(respiratory)"
        :initial-form="state.condition"
        :diagnosis-date="format(minSubConditionDate, defaultDateFormat)"
        @update:form="onConditionUpdate"
    />
</template>

<style>

</style>
